import { isMobileBrowser } from '@/utils/browser-family'

export type CaptureCompatibilityIssue = 'mobile' | 'insecure-context' | 'embedded' | 'unsupported' | 'policy-blocked'

interface CapturePolicy {
  features?: () => string[]
  allowsFeature?: (feature: string) => boolean
}

export interface CaptureCompatibilityEnvironment {
  readonly mobile?: boolean
  readonly topLevel?: boolean
}

/** Only explicit, observable restrictions block capture. Unknown capabilities may be tried. */
export function checkCaptureCompatibility(
  secureContext: boolean | undefined = globalThis.isSecureContext,
  mediaDevices: { getDisplayMedia?: unknown } | undefined = globalThis.navigator?.mediaDevices,
  policy: CapturePolicy | undefined = typeof document === 'undefined' ? undefined
    : (document as Document & { permissionsPolicy?: CapturePolicy; featurePolicy?: CapturePolicy }).permissionsPolicy
      ?? (document as Document & { featurePolicy?: CapturePolicy }).featurePolicy,
  environment: CaptureCompatibilityEnvironment = {},
): CaptureCompatibilityIssue | null {
  const mobile = environment.mobile ?? (typeof navigator === 'undefined' ? false : isMobileBrowser())
  const topLevel = environment.topLevel ?? (typeof window === 'undefined' || window.top === window)
  if (mobile) return 'mobile'
  if (secureContext === false) return 'insecure-context'
  if (!topLevel) return 'embedded'
  if (typeof mediaDevices?.getDisplayMedia !== 'function') return 'unsupported'
  try {
    // Unknown feature names can return false, so first verify this policy API knows capture.
    if (policy?.features?.().includes('display-capture') && policy.allowsFeature?.('display-capture') === false) {
      return 'policy-blocked'
    }
  } catch { /* Policy introspection is optional, not evidence of denial. */ }
  return null
}

export class CaptureCompatibilityError extends Error {
  constructor(readonly kind: CaptureCompatibilityIssue) {
    super(kind)
    this.name = 'CaptureCompatibilityError'
  }
}
